import { useEffect, useState, type ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
import {
  initI18n,
  refreshI18nResources,
  directionForLocale,
  normalizeLocale,
  readPersistedLocale,
  writePersistedLocale,
} from '@dukanti/shared/i18n';
import { installAdminI18nResources } from '@dukanti/shared/i18n/admin-resources';
import { installStorefrontPublishFieldDiffI18n } from './storefront-publish-field-diff-i18n';

export function I18nProvider({ children }: { children: ReactNode }) {
  const [i18n] = useState(() => {
    const instance = initI18n({ locale: readPersistedLocale() });
    installAdminI18nResources(instance);
    installStorefrontPublishFieldDiffI18n(instance);
    return instance;
  });

  useEffect(() => {
    refreshI18nResources(i18n);
    installAdminI18nResources(i18n);
    installStorefrontPublishFieldDiffI18n(i18n);
  });

  useEffect(() => {
    const sync = (rawLanguage?: string, persist = false) => {
      const lang = normalizeLocale(rawLanguage ?? i18n.language);
      document.documentElement.lang = lang;
      document.documentElement.dir = directionForLocale(lang);
      if (persist) writePersistedLocale(lang);
    };

    const handleLanguageChanged = (rawLanguage?: string) => sync(rawLanguage, true);

    i18n.on('languageChanged', handleLanguageChanged);

    const persisted = readPersistedLocale();
    if (normalizeLocale(i18n.language) !== persisted) {
      void i18n.changeLanguage(persisted);
    } else {
      sync();
    }

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
